<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'
import { useEqStore } from '@/stores/eq'
import { useChijiStore } from '@/stores/chiji'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import SidetoneControl from './SidetoneControl.vue'

const { t } = useI18n()
const deviceStore = useDeviceStore()
const eqStore = useEqStore()
const chijiStore = useChijiStore()
const route = useRoute()
const footerPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).has('footerPreview')

// 菜单状态
const showEqDropdown = ref(false)
const showRgbDropdown = ref(false)
const showVolumePopup = ref(false)
const showMicPopup = ref(false)

const noisePopupEl = ref<HTMLElement | null>(null)
const noiseTrackEl = ref<HTMLElement | null>(null)
const noiseThumbEl = ref<HTMLElement | null>(null)
const noiseProgressEl = ref<HTMLElement | null>(null)
const noiseValueEl = ref<HTMLElement | null>(null)

const noiseDragging = ref(false)
let lastNoiseLevelSent: number | null = null

const activeFooterPopup = ref<'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix' | null>(null)

let hideTimer: number | null = null

const clearHideTimer = () => {
  if (hideTimer != null) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
}

const closeAllPopups = () => {
  clearHideTimer()
  showEqDropdown.value = false
  showRgbDropdown.value = false
  showVolumePopup.value = false
  showMicPopup.value = false
  activeFooterPopup.value = null
}

const openPopup = (popup: 'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix') => {
  clearHideTimer()
  if (!audioHelpPinned.value) showAudioHelp.value = false
  activeFooterPopup.value = popup

  showEqDropdown.value = popup === 'eq'
  showRgbDropdown.value = popup === 'rgb'
  showVolumePopup.value = popup === 'volume'
  showMicPopup.value = popup === 'mic'
}

const scheduleClosePopup = (popup: 'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix') => {
  clearHideTimer()
  hideTimer = window.setTimeout(() => {
    if (activeFooterPopup.value === popup) {
      closeAllPopups()
    }
  }, 300)
}

const audioDragging = ref(false)

const clampAudioValue = (v: number) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(100, n))
}

const getAudioBalanceSendValue = (positionValue: number) => {
  const pos = clampAudioValue(positionValue)
  return Math.max(0, Math.min(200, Math.round(pos * 2)))
}

let lastAudioBalanceSentValue: number | null = null

// 旧版显示值：中心(50)=>100/100；往左衰减左侧；往右衰减右侧
const getAudioBalanceDisplayValue = (positionValue: number) => {
  const pos = clampAudioValue(positionValue)
  const distanceFromCenter = Math.abs(pos - 50)
  return Math.max(0, Math.min(100, Math.round(100 - (distanceFromCenter / 50) * 100)))
}

const audioSliderEl = ref<HTMLElement | null>(null)
const audioModeMenuEl = ref<HTMLElement | null>(null)

const leftChannelLabel = computed(() => '2.4G')

const audioBalanceValue = computed(() => clampAudioValue(deviceStore.audioBalanceValue))

const leftDisplayPercent = computed(() => {
  const pos = audioBalanceValue.value
  const displayValue = getAudioBalanceDisplayValue(pos)
  if (pos < 50) return displayValue
  return 100
})

const rightDisplayPercent = computed(() => {
  const pos = audioBalanceValue.value
  const displayValue = getAudioBalanceDisplayValue(pos)
  if (pos > 50) return displayValue
  return 100
})

const applyAudioSliderCssVars = (value?: number) => {
  const el = audioSliderEl.value
  if (!el) return
  const pos = clampAudioValue(value ?? audioBalanceValue.value)
  el.style.setProperty('--ab-pos', String(pos))
  if (pos >= 50) {
    el.style.setProperty('--ab-fill-start', '50')
    el.style.setProperty('--ab-fill-width', String(pos - 50))
  } else {
    el.style.setProperty('--ab-fill-start', String(pos))
    el.style.setProperty('--ab-fill-width', String(50 - pos))
  }
}

watch(
  () => deviceStore.audioBalanceValue,
  () => {
    applyAudioSliderCssVars()
  },
)

const setAudioBalanceByClientX = (clientX: number, shouldSend: boolean) => {
  const el = audioSliderEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (!rect.width) return
  const percent = ((clientX - rect.left) / rect.width) * 100
  const nextValue = clampAudioValue(percent)

  applyAudioSliderCssVars(nextValue)
  if (shouldSend) {
    const nextSendValue = getAudioBalanceSendValue(nextValue)
    if (lastAudioBalanceSentValue !== nextSendValue) {
      lastAudioBalanceSentValue = nextSendValue
      void deviceStore.setAudioBalance(nextValue)
    }
  }
}

const onAudioSliderClick = (e: MouseEvent) => {
  setAudioBalanceByClientX(e.clientX, true)
}

const onAudioPointerDown = (e: PointerEvent) => {
  const target = e.target as HTMLElement
  const isThumb = target?.classList?.contains('audio-balance-thumb')
  const inSlider = !!(target && (target.classList?.contains('audio-balance-slider') || target.closest?.('.audio-balance-slider')))
  if (!isThumb && !inSlider) return

  audioDragging.value = true
    ; (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  setAudioBalanceByClientX(e.clientX, true)
}

const onAudioPointerMove = (e: PointerEvent) => {
  if (!audioDragging.value) return
  setAudioBalanceByClientX(e.clientX, true)
}

const stopAudioDragging = () => {
  audioDragging.value = false

  // 对齐旧版：拖动结束后，如果鼠标已离开容器，则关闭弹窗
  const container = document.querySelector('.audio-mode-container') as HTMLElement | null
  if (container && !container.matches(':hover') && activeFooterPopup.value === 'audioMix') {
    closeAllPopups()
  }
}

// EQ模式选项 - 对齐原项目
const eqModes = [
  { id: 'game', label: t('eq.game', '游戏') },
  { id: 'music', label: t('eq.music', '音乐') },
  { id: 'movie', label: t('eq.movie', '电影') },
]

// 计算当前EQ显示文本
const currentEqLabel = computed(() => {
  const mode = eqStore.activeMode
  if (!eqStore.currentModeState?.enabled) return 'OFF'
  const found = eqModes.find(m => m.id === mode)
  return found?.label || 'OFF'
})

// RGB模式选项 - 对齐原项目（耳机和麦克风不同）
const headphoneRgbModes = [
  // 下拉菜单顺序与旧版一致：当前蓝色节奏在最上方，随后是青色、紫色。
  { id: 'chase', label: t('mainrgb.marquee', 'Blue Rhythm') },
  { id: 'gradient', label: t('mainrgb.gradient', 'Cyan Rhythm') },
  { id: 'breathing', label: t('mainrgb.breathing', 'Purple Rhythm') },
]

const micRgbModes = [
  { id: 'spectrum', label: t('mainrgb.mic_gradient', 'Spectrum') },
  { id: 'breathing', label: t('mainrgb.mic_breathing', 'Breathing') },
  { id: 'red', label: t('mainrgb.red', 'Red') },
  { id: 'blue', label: t('mainrgb.blue', 'Blue') },
  { id: 'green', label: t('mainrgb.green', 'Green') },
  { id: 'purple', label: t('mainrgb.purple', 'Purple') },
  { id: 'yellow', label: t('mainrgb.yellow', 'Yellow') },
  { id: 'cyan', label: t('mainrgb.cyan', 'Cyan') },
  { id: 'white', label: t('mainrgb.white', 'White') },
]

const rgbModes = computed(() => {
  return deviceStore.isHeadPhone ? headphoneRgbModes : micRgbModes
})

// 对齐旧版 server-1.js：RGB 模式字节映射
// 00=关闭，01=追逐，02=渐变/频谱，03=呼吸，04..0A=单色
const rgbIdToModeByte: Record<string, number> = {
  off: 0x00,
  chase: 0x01,
  gradient: 0x02,
  spectrum: 0x02,
  breathing: 0x03,
  red: 0x04,
  blue: 0x05,
  green: 0x06,
  purple: 0x07,
  yellow: 0x08,
  cyan: 0x09,
  white: 0x0A,
}

const rgbModeByteToId = (mode: number): string => {
  if (mode === 0x01) return 'chase'
  if (mode === 0x02) return deviceStore.isHeadPhone ? 'gradient' : 'spectrum'
  if (mode === 0x03) return 'breathing'
  if (mode === 0x04) return 'red'
  if (mode === 0x05) return 'blue'
  if (mode === 0x06) return 'green'
  if (mode === 0x07) return 'purple'
  if (mode === 0x08) return 'yellow'
  if (mode === 0x09) return 'cyan'
  if (mode === 0x0A) return 'white'
  return 'off'
}

const currentRgbLabel = computed(() => {
  if (!deviceStore.rgbEnabled) return 'OFF'
  const modeId = rgbModeByteToId(deviceStore.rgbMode)
  const mode = rgbModes.value.find(m => m.id === modeId)
  return mode?.label || 'OFF'
})

// 降噪档位显示
const noiseLevelText = computed(() => {
  const level = deviceStore.noiseLevel || 1
  const unit = String(t('footer.level', '档'))
  return unit.toLowerCase() === 'level'
    ? `<span class="unit">${unit}</span> ${level}`
    : `${level}<span class="unit">${unit}</span>`
})

const noiseLevel = computed(() => deviceStore.noiseLevel || 1)

const applyNoiseSliderUI = (level: number) => {
  const clamped = Math.max(1, Math.min(6, Math.round(level)))
  const percent = ((clamped - 1) / 5) * 100

  if (noiseThumbEl.value) noiseThumbEl.value.style.left = `${percent}%`
  if (noiseProgressEl.value) noiseProgressEl.value.style.width = `${percent}%`
  if (noiseValueEl.value) noiseValueEl.value.textContent = String(clamped)
}

const setNoiseLevelByClientX = (clientX: number, shouldSend: boolean) => {
  const el = noiseTrackEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (!rect.width) return
  const ratio = (clientX - rect.left) / rect.width
  const nextLevel = Math.max(1, Math.min(6, Math.round(ratio * 5) + 1))

  applyNoiseSliderUI(nextLevel)

  if (deviceStore.noiseEnabled) {
    if (shouldSend) {
      if (lastNoiseLevelSent !== nextLevel) {
        lastNoiseLevelSent = nextLevel
        void deviceStore.setNoiseLevel(nextLevel)
      }
    }
  } else {
    void deviceStore.setNoiseLevel(nextLevel)
  }
}

const onNoisePointerDown = (e: PointerEvent) => {
  noiseDragging.value = true
  openPopup('noise')
  lastNoiseLevelSent = null
    ; (e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  setNoiseLevelByClientX(e.clientX, true)
}

const onNoisePointerMove = (e: PointerEvent) => {
  if (!noiseDragging.value) return
  setNoiseLevelByClientX(e.clientX, true)
}

const stopNoiseDragging = () => {
  if (!noiseDragging.value) return
  noiseDragging.value = false

  const container = document.querySelector('.noise-container') as HTMLElement | null
  if (container && !container.matches(':hover') && activeFooterPopup.value === 'noise') {
    closeAllPopups()
  }
}

const onNoiseTrackClick = (e: MouseEvent) => {
  lastNoiseLevelSent = null
  setNoiseLevelByClientX(e.clientX, true)
}

const incNoiseLevel = () => {
  const next = Math.min(6, noiseLevel.value + 1)
  applyNoiseSliderUI(next)
  if (deviceStore.noiseEnabled) {
    void deviceStore.setNoiseLevel(next)
  } else {
    void deviceStore.setNoiseLevel(next)
  }
}

const decNoiseLevel = () => {
  const next = Math.max(1, noiseLevel.value - 1)
  applyNoiseSliderUI(next)
  if (deviceStore.noiseEnabled) {
    void deviceStore.setNoiseLevel(next)
  } else {
    void deviceStore.setNoiseLevel(next)
  }
}

const chooseNoiseLevel = (level: number) => {
  const next = Math.max(1, Math.min(6, Math.round(level)))
  applyNoiseSliderUI(next)
  lastNoiseLevelSent = null
  void deviceStore.setNoiseLevel(next)
}

// 音量值（与设备状态同步）
const volume = computed({
  get: () => deviceStore.speakerVolume,
  set: (v: number) => {
    void deviceStore.setSpeakerVolume(v)
  },
})

const micVolume = computed({
  get: () => deviceStore.micVolume,
  set: (v: number) => {
    void deviceStore.setMicVolume(v)
  },
})

onMounted(() => {
  void nextTick(() => {
    applyAudioSliderCssVars()
    applyNoiseSliderUI(deviceStore.noiseLevel || 1)
  })
  document.addEventListener('pointerup', stopAudioDragging)
  document.addEventListener('pointercancel', stopAudioDragging)
  document.addEventListener('pointerup', stopNoiseDragging)
  document.addEventListener('pointercancel', stopNoiseDragging)
  document.addEventListener('pointerdown', onDocumentPointerDown)
  document.addEventListener('keydown', onDocumentKeydown)
})

onUnmounted(() => {
  clearHideTimer()
  document.removeEventListener('pointerup', stopAudioDragging)
  document.removeEventListener('pointercancel', stopAudioDragging)
  document.removeEventListener('pointerup', stopNoiseDragging)
  document.removeEventListener('pointercancel', stopNoiseDragging)
  document.removeEventListener('pointerdown', onDocumentPointerDown)
  document.removeEventListener('keydown', onDocumentKeydown)
  if (audioHelpHideTimer != null) window.clearTimeout(audioHelpHideTimer)
})

// 切换电源 - 对齐原项目
const togglePower = async () => {
  // 旧版：USB 模式下提示“无效”
  if (deviceStore.connectionType === 'USB') {
    ElMessage({
      message: 'Ineffective',
      duration: 1200,
    })
    return
  }

  // 旧版：只有 2.4G + 支持电源控制才可用
  if (!deviceStore.canUsePowerControl) return

  // 旧版：开机 -> 轻提示 4.5 秒；关机 -> 确认弹窗
  if (deviceStore.isPowerOff) {
    ElMessage({
      message: route.path === '/' ? (t('footer.poweringOn', 'Powering on...') as any) : (t('footer.poweringOn', 'Powering on...') as any),
      duration: 4500,
    })
    await deviceStore.setPowerOn(true)
    return
  }

  try {
    await ElMessageBox.confirm(
      `
        <div style="text-align: left;">
          <div style="font-size: 16px;margin-bottom: 8px;">${t('footer.powerOffConfirmTitle', '确定要进行设备关机吗？')}</div>
          <div style="font-size: 14px; color:rgba(255, 255, 255, 0.75);">${t('footer.powerOffConfirmDesc', '确认关机后，将会进入弱连接状态，此时耳机会有一定功耗。')}</div>
        </div>
      `,
      '',
      {
        confirmButtonText: t('footer.powerOffConfirmBtn', '确认关机') as any,
        cancelButtonText: t('footer.cancel', '取消') as any,
        dangerouslyUseHTMLString: true,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: true,
      },
    )
  } catch {
    return
  }

  await deviceStore.setPowerOn(false)
}

// 旧版：点击 EQ 主按钮做开/关切换（不改变当前模式）
const toggleEqEnabled = async () => {
  await eqStore.setEqEnabled(!eqStore.isEnabled)
}

// 旧版：点击 RGB 主按钮做开/关切换（不改变当前模式）
const toggleRgbEnabled = async () => {
  await deviceStore.setRgbEnabled(!deviceStore.rgbEnabled)
}

// 切换EQ模式
const switchEqMode = (modeId: string) => {
  if (modeId === 'off') {
    void eqStore.setEqEnabled(false)
  } else {
    eqStore.setActiveMode(modeId as any)
    void eqStore.setEqEnabled(true)
  }

  // 选中菜单项后保持当前下拉菜单打开；只有鼠标真正移出菜单区域时才关闭。
  openPopup('eq')
}

// 切换RGB模式
const switchRgbMode = async (modeId: string) => {
  const modeByte = rgbIdToModeByte[modeId] ?? 0x00

  if (modeId === 'off') {
    await deviceStore.setRgbEnabled(false)
  } else if (deviceStore.rgbEnabled) {
    // 已开启时只切模式即可。上一版这里还会再 setRgbEnabled(true)，
    // 同一次点击会重复下发两条 RGB 命令，容易与状态轮询交错造成闪烁。
    await deviceStore.setRgbMode(modeByte)
  } else {
    // 关闭状态选灯效：先只记住模式，再开启并由 setRgbEnabled 统一下发一次。
    await deviceStore.setRgbMode(modeByte)
    await deviceStore.setRgbEnabled(true)
  }

  // 点击只切换选中项，不立即收起悬浮菜单。
  openPopup('rgb')
}

// 对齐旧版：点击图标直接开/关 AI 降噪（不弹下拉）
const toggleNoiseEnabled = async () => {
  await deviceStore.setNoiseEnabled(!deviceStore.noiseEnabled)
  void nextTick(() => {
    lastNoiseLevelSent = null
    applyNoiseSliderUI(deviceStore.noiseLevel || 1)
  })
}

// 打开一键吃鸡
const openChiji = async () => {
  await chijiStore.setEnabled(!chijiStore.enabled)
}

watch(
  () => route.path,
  () => {
    closeAllPopups()
  },
)

// 对齐旧版: 点击图标切换静音
const toggleSpeakerMute = async () => {
  await deviceStore.toggleSpeakerMute()
}

const toggleMicMute = async () => {
  await deviceStore.toggleMicMute()
}

const speakerIconSrc = computed(() => {
  return deviceStore.speakerMuted ? '/img/footer/footer_voice_off.png' : '/img/footer/footer_voice.png'
})

const micIconSrc = computed(() => {
  return deviceStore.micMuted ? '/img/footer/footer_mic_off.png' : '/img/footer/footer_mic.png'
})

const isEqEnabled = computed(() => !!eqStore.currentModeState?.enabled)
const isRgbEnabled = computed(() => !!deviceStore.rgbEnabled)
const isNoiseEnabled = computed(() => !!deviceStore.noiseEnabled)
const isBridgeEnabled = computed(() => deviceStore.deviceStatus?.callBridgeEnabled === 1)
const isAudioShareEnabled = computed(() => deviceStore.deviceStatus?.btShareEnabled === 1)

const audioModePillStyle = computed(() => {
  const mode = footerPreview ? 2 : deviceStore.audioMode
  if (mode === 1) return { '--active-left': '83px', '--active-width': '80px' }
  if (mode === 2) return { '--active-left': '163px', '--active-width': '92px' }
  return { '--active-left': '3px', '--active-width': '80px' }
})

const setAudioMode = async (mode: 0 | 1 | 2) => {
  await deviceStore.setAudioMode(mode)
}

// 帮助和提示相关
const showAudioHelp = ref(false)
const audioHelpPinned = ref(false)
const audioHelpEl = ref<HTMLElement | null>(null)
let audioHelpHideTimer: number | null = null

function toggleAudioHelp() {
  audioHelpPinned.value = !audioHelpPinned.value
  showAudioHelp.value = audioHelpPinned.value
}

function openAudioHelp() {
  if (audioHelpHideTimer != null) window.clearTimeout(audioHelpHideTimer)
  closeAllPopups()
  showAudioHelp.value = true
}

function closeAudioHelp() {
  if (audioHelpPinned.value) return
  if (audioHelpHideTimer != null) window.clearTimeout(audioHelpHideTimer)
  audioHelpHideTimer = window.setTimeout(() => {
    showAudioHelp.value = false
  }, 120)
}

function onDocumentPointerDown(e: PointerEvent) {
  if (!audioHelpPinned.value || audioHelpEl.value?.contains(e.target as Node)) return
  audioHelpPinned.value = false
  showAudioHelp.value = false
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !audioHelpPinned.value) return
  audioHelpPinned.value = false
  showAudioHelp.value = false
}

function enterAudioFeature() {
  closeAllPopups()
  if (audioHelpPinned.value) return
  if (audioHelpHideTimer != null) window.clearTimeout(audioHelpHideTimer)
  showAudioHelp.value = false
}

function toggleBridge() {
  const enabled = deviceStore.deviceStatus?.callBridgeEnabled === 1
  void deviceStore.setCallBridge(!enabled)
}

function toggleAudioShare() {
  const enabled = deviceStore.deviceStatus?.btShareEnabled === 1
  void deviceStore.setBtShare(!enabled)
}
</script>

<template>
  <footer v-if="footerPreview || (deviceStore.isConnected && deviceStore.isDeviceConnected && route.path === '/')"
    class="bottom-menu" :class="{ 'is-preview': footerPreview }">
    <div class="footer-menu">
      <!-- 电源按钮 -->
      <div v-if="footerPreview || deviceStore.canUsePowerControl" class="footer-menu-container">
        <div class="icon-box icon-box-on" @click="togglePower">
          <a href="javascript:;" class="footer-power">
            <img alt="电源" class="footer-icon" src="/img/footer/on_icon.png">
          </a>
        </div>
      </div>

      <!-- EQ切换 -->
      <div v-if="footerPreview || deviceStore.canUseEq" class="footer-menu-container eq-container">
        <div class="icon-box" :class="{ selected: isEqEnabled }" @click.stop="toggleEqEnabled"
          @mouseenter="openPopup('eq')" @mouseleave="scheduleClosePopup('eq')">
          <a href="javascript:;">
            <img alt="EQ" class="footer-icon" :class="{ 'footer-EQ-on': isEqEnabled }"
              src="/img/footer/footer_EQ_icon.png">
          </a>
          <span class="footer-text">{{ currentEqLabel }}</span>
        </div>
        <!-- EQ下拉菜单 -->
        <div v-if="showEqDropdown" class="popup-dropdown" @mouseenter="openPopup('eq')"
          @mouseleave="scheduleClosePopup('eq')">
          <div v-for="mode in eqModes" :key="mode.id" class="dropdown-item"
            :class="{ active: isEqEnabled && eqStore.activeMode === mode.id }" @click.stop="switchEqMode(mode.id)">
            {{ mode.label }}
          </div>
        </div>
      </div>

      <!-- RGB控制 -->
      <div v-if="footerPreview || deviceStore.canUseLed" class="footer-menu-container rgb-container">
        <div class="icon-box" :class="{ selected: footerPreview || isRgbEnabled }" @click.stop="toggleRgbEnabled"
          @mouseenter="openPopup('rgb')" @mouseleave="scheduleClosePopup('rgb')">
          <a href="javascript:;">
            <img alt="RGB" class="footer-icon" :class="{ 'footer-RGB-on': footerPreview || isRgbEnabled }"
              src="/img/footer/footer_rgb_icon.png">
          </a>
          <span class="footer-text">{{ footerPreview ? 'Blue Rhythm' : currentRgbLabel }}</span>
        </div>
        <!-- RGB下拉菜单 -->
        <div v-if="showRgbDropdown" class="popup-dropdown" @mouseenter="openPopup('rgb')"
          @mouseleave="scheduleClosePopup('rgb')">
          <div v-for="mode in rgbModes" :key="mode.id" class="dropdown-item"
            :class="{ active: isRgbEnabled && rgbModeByteToId(deviceStore.rgbMode) === mode.id }"
            @click.stop="switchRgbMode(mode.id)">
            {{ mode.label }}
          </div>
        </div>
      </div>

      <!-- AI降噪档位 -->
      <div v-if="footerPreview || deviceStore.canUseNoiseReduction" class="footer-menu-container noise-container">
        <div class="icon-box"
          :class="{ selected: footerPreview || isNoiseEnabled, 'is-popup-open': activeFooterPopup === 'noise' || noiseDragging }"
          @click.stop="toggleNoiseEnabled">
          <a href="javascript:;">
            <img alt="AI降噪" class="footer-icon" :class="{ 'footer-ENC-on': footerPreview || isNoiseEnabled }"
              src="/img/footer/footer_ENC_icon.png">
          </a>
          <span class="footer-text noise-level" v-html="noiseLevelText"></span>
        </div>


      </div>

      <!-- 音量控制 -->
      <div class="footer-menu-container volume-container round-control">
        <div class="hover-button" @mouseenter="openPopup('volume')" @mouseleave="scheduleClosePopup('volume')">
          <a href="javascript:;">
            <img alt="音量" class="footer-icon round-icon speaker-icon" :class="{ muted: deviceStore.speakerMuted }"
              :src="speakerIconSrc" @click.stop="toggleSpeakerMute">
          </a>
        </div>
        <!-- 音量弹窗 -->
        <div v-if="showVolumePopup" class="popup-window" @mouseenter="openPopup('volume')"
          @mouseleave="scheduleClosePopup('volume')">
          <div class="slider-container">
            <el-slider v-model="volume" vertical height="130px" :max="100" :min="0"
              @change="deviceStore.setSpeakerVolume" />
          </div>
          <div class="value-text">{{ volume }}%</div>
        </div>
      </div>

      <!-- 麦克风控制 -->
      <div class="footer-menu-container mic-container round-control">
        <div class="hover-button" @mouseenter="openPopup('mic')" @mouseleave="scheduleClosePopup('mic')">
          <a href="javascript:;">
            <img alt="麦克风" class="footer-icon mic-icon" :class="{ muted: deviceStore.micMuted }" :src="micIconSrc"
              @click.stop="toggleMicMute">
          </a>
        </div>
        <!-- 麦克风弹窗 -->
        <div v-if="showMicPopup" class="popup-window" @mouseenter="openPopup('mic')"
          @mouseleave="scheduleClosePopup('mic')">
          <div class="slider-container">
            <el-slider v-model="micVolume" vertical height="130px" :max="100" :min="0"
              @change="deviceStore.setMicVolume" />
          </div>
          <div class="value-text">{{ micVolume }}%</div>
        </div>
      </div>

      <!-- 耳返控制 - 只在耳机模式显示 -->
      <SidetoneControl v-if="footerPreview || deviceStore.isHeadPhone"
        class="footer-menu-container round-control sidetone-control" />

      <!-- Audio Mix -->
      <div ref="audioModeMenuEl" v-if="footerPreview || deviceStore.canUseSpecialAudio"
        class="footer-menu-container audio-mode-container"
        :class="{ 'is-popup-open': activeFooterPopup === 'audioMix' || audioDragging }"
        @mouseenter="openPopup('audioMix')" @mouseleave="scheduleClosePopup('audioMix')">
        <div class="audio-balance-popup">
          <div class="audio-balance-labels">
            <span class="audio-balance-left">
              <span class="audio-label">{{ leftChannelLabel }}:</span> {{ leftDisplayPercent }}%
            </span>
            <span class="audio-balance-divider">|</span>
            <span class="audio-balance-right"><span class="audio-label">USB:</span> {{ rightDisplayPercent }}%</span>
          </div>

          <div ref="audioSliderEl" class="audio-balance-slider" @click.stop="onAudioSliderClick"
            @pointerdown.stop.prevent="onAudioPointerDown" @pointermove.stop.prevent="onAudioPointerMove">
            <div class="audio-balance-track"></div>
            <div class="audio-balance-fill audio-balance-fill-right"></div>
            <div class="audio-balance-thumb"></div>
          </div>
        </div>

        <div class="audio-mode-switch audio-mode-segment" :style="audioModePillStyle">
          <span class="audio-mode-active-pill" aria-hidden="true"></span>
          <button type="button" class="audio-mode-btn"
            :class="{ 'is-active': !footerPreview && deviceStore.audioMode === 0 }"
            @click.stop="setAudioMode(0)">Mix</button>
          <button type="button" class="audio-mode-btn"
            :class="{ 'is-active': !footerPreview && deviceStore.audioMode === 1 }"
            @click.stop="setAudioMode(1)">Split</button>
          <button type="button" class="audio-mode-btn"
            :class="{ 'is-active': footerPreview || deviceStore.audioMode === 2 }"
            @click.stop="setAudioMode(2)">Adaptive</button>
        </div>
      </div>

      <!-- 帮助按钮 -->
      <div ref="audioHelpEl" class="footer-menu-container help-container" :class="{ 'is-pinned': audioHelpPinned }"
        @mouseenter="openAudioHelp" @mouseleave="closeAudioHelp">
        <button class="audio-help-button" type="button" :aria-expanded="showAudioHelp" @click.stop="toggleAudioHelp"
          aria-label="Audio Modes help">
          ?
        </button>

        <!-- 音频模式帮助弹窗 -->
        <Transition name="popup-fade">
          <div v-if="showAudioHelp" class="audio-info-popup audio-modes-help-popup" role="tooltip"
            @mouseenter="openAudioHelp" @mouseleave="closeAudioHelp" @click.stop>
            <h3>Audio Modes</h3>
            <p>These modes change how available audio sources are distributed. Final diagrams should follow the
              confirmed hardware routing rules.</p>
            <div class="audio-mode-help-cards">
              <div class="audio-mode-help-card">
                <strong>Mix</strong>
                <span>Keep available audio together.</span>
              </div>
              <div class="audio-mode-help-card">
                <strong>Split</strong>
                <span>Separate audio sources by channel.</span>
              </div>
              <div class="audio-mode-help-card">
                <strong>Adaptive</strong>
                <span>Use the headset's adaptive audio behavior.</span>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Bridge 按钮 -->
      <div class="footer-menu-container audio-feature-item bridge-feature-item" @mouseenter="enterAudioFeature">
        <a href="javascript:;" class="audio-feature-btn bridge-feature-btn" :class="{ 'is-active': isBridgeEnabled }"
          :aria-pressed="isBridgeEnabled" @click="toggleBridge">
          <svg class="audio-feature-icon icon-call-bridge" width="24" height="24" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M7.74931 7.20774C7.09835 7.53323 6.61012 7.94033 6.44738 8.67267C5.79642 11.5206 8.64439 15.996 11.4924 17.7048C13.0384 18.5999 14.5031 18.844 15.3982 18.3558L17.0256 17.3793C17.2885 17.2115 17.4772 16.9493 17.5529 16.6466C17.6285 16.3439 17.5854 16.0238 17.4324 15.7519L16.3746 14.1245C16.209 13.8821 15.9614 13.7075 15.6775 13.6327C15.3935 13.558 15.0921 13.5882 14.8286 13.7177L13.3639 14.5314C12.3061 13.9618 10.8414 12.4971 10.1904 11.3579L11.0041 9.89323C11.1336 9.62969 11.1638 9.3283 11.0891 9.04433C11.0143 8.76036 10.8397 8.51285 10.5973 8.34718L8.96988 7.28936C8.79158 7.16961 8.58483 7.09902 8.37052 7.08473C8.15622 7.07045 7.94193 7.11271 7.74931 7.20774Z"
              stroke="white" stroke-opacity="0.6" stroke-width="1.1" stroke-linecap="square" stroke-linejoin="round" />
            <path d="M18.1629 9.57004H13.9316L15.6353 11.4414" stroke="white" stroke-opacity="0.6" stroke-width="1.1"
              stroke-linecap="square" stroke-linejoin="round" />
            <path d="M13.9328 7.37137H18.1641L16.4604 5.5" stroke="white" stroke-opacity="0.6" stroke-width="1.1"
              stroke-linecap="square" stroke-linejoin="round" />
          </svg>
          <span>Bridge</span>
        </a>

        <div class="audio-feature-tooltip" role="tooltip">
          <h3>Bridge</h3>
          <p>Bring conversations across 2.4G and USB together, so people using different apps can hear each other.</p>
        </div>
      </div>

      <!-- Audio Share 按钮 -->
      <div class="footer-menu-container audio-feature-item share-feature-item" @mouseenter="enterAudioFeature">
        <a href="javascript:;" class="audio-feature-btn share-feature-btn" :class="{ 'is-active': isAudioShareEnabled }"
          :aria-pressed="isAudioShareEnabled" @click="toggleAudioShare">
          <svg class="audio-feature-icon icon-audio-share" width="24" height="24" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M15.1992 17.5977H14V6.39844H15.1992V17.5977ZM9.02344 8.57422L12.0234 11.5742C12.2578 11.8085 12.2578 12.1876 12.0234 12.4219L9.02344 15.4219L8.59961 15.8467L7.75098 14.998L8.17578 14.5742L10.1523 12.5977H5V11.3984H10.1523L8.17578 9.42188L7.75098 8.99805L8.59961 8.14941L9.02344 8.57422ZM17.9961 8.54785C18.4856 8.97952 18.878 9.51064 19.1465 10.1055C19.415 10.7003 19.5537 11.3454 19.5537 11.998C19.5537 12.6507 19.415 13.2958 19.1465 13.8906C18.878 14.4855 18.4856 15.0166 17.9961 15.4482L17.5469 15.8447L16.7529 14.9453L17.2031 14.5479C17.5648 14.2288 17.8543 13.836 18.0527 13.3965C18.2511 12.9569 18.3535 12.4803 18.3535 11.998C18.3535 11.5158 18.2511 11.0392 18.0527 10.5996C17.8543 10.1601 17.5648 9.76726 17.2031 9.44824L16.7529 9.05078L17.5469 8.15137L17.9961 8.54785Z"
              fill="white" fill-opacity="0.6" />
          </svg>
          <span>Audio Share</span>
        </a>

        <div class="audio-feature-tooltip" role="tooltip">
          <h3>Audio Share</h3>
          <p>Share Bluetooth audio with the 2.4G / USB side.</p>
        </div>
      </div>

      <!-- 一键吃鸡 -->
      <div v-if="footerPreview || deviceStore.canUseSpecialAudio"
        class="footer-menu-container audio-feature-item positioning-container" @mouseenter="enterAudioFeature">
        <div class="chiji-mode-switch">
          <a href="javascript:;" id="xuni71" class="audio-feature-btn chiji-mode-btn footer-menu-item"
            :class="{ 'is-active': footerPreview || chijiStore.enabled }" @click.stop.prevent="openChiji">
            <svg class="audio-feature-icon icon-positioning" width="24" height="24" viewBox="0 0 24 24" fill="none"
              xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <g clip-path="url(#clip0_8078_63749)">
                <path
                  d="M12.8682 5.46191C13.0416 5.47778 13.2149 5.49952 13.3877 5.5293C15.0077 5.80864 16.4656 6.68284 17.4736 7.98145C18.2958 9.04076 18.7708 10.3197 18.8467 11.6445H20.7275V12.8438H18.8057C18.6136 14.3699 17.8928 15.7852 16.7637 16.8389C15.7075 17.8242 14.3608 18.4304 12.9346 18.5752L12.9336 20.043H12.334L11.7334 20.042V17.416L12.3271 17.4092C13.6714 17.3946 14.9624 16.878 15.9453 15.9609C16.9281 15.0437 17.5332 13.7913 17.6406 12.4512C17.7479 11.1114 17.3503 9.77967 16.5264 8.71777C15.702 7.65566 14.5095 6.93943 13.1846 6.71094C11.8597 6.48251 10.4967 6.7578 9.36426 7.48242C8.23197 8.20709 7.41114 9.32934 7.06348 10.6279C6.71597 11.9266 6.86646 13.3086 7.48535 14.502C8.10434 15.6953 9.14743 16.6141 10.4092 17.0781L10.2031 17.6416L9.99512 18.2051C8.45231 17.6378 7.17683 16.5139 6.41992 15.0547C6.05876 14.3583 5.82974 13.6091 5.7334 12.8438H4.01953V11.6445H5.69336C5.71884 11.1992 5.78737 10.7542 5.9043 10.3174C6.32934 8.72965 7.33245 7.35775 8.7168 6.47168C9.61244 5.89854 10.6264 5.55601 11.6689 5.46094V4H12.8682V5.46191ZM12.2627 9.63965C13.5784 9.63965 14.6455 10.7058 14.6455 12.0215C14.6455 13.3372 13.5784 14.4043 12.2627 14.4043C10.9472 14.404 9.88088 13.337 9.88086 12.0215C9.88091 10.706 10.9473 9.63995 12.2627 9.63965ZM12.2627 10.8389C11.61 10.8392 11.0801 11.3687 11.0801 12.0215C11.0801 12.6743 11.61 13.2038 12.2627 13.2041C12.9157 13.2041 13.4453 12.6744 13.4453 12.0215C13.4453 11.3686 12.9156 10.8389 12.2627 10.8389Z"
                  fill="white" fill-opacity="0.6" />
              </g>
              <defs>
                <clipPath id="clip0_8078_63749">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            {{ t('main.chickenDinner', 'Positioning') }}
          </a>
        </div>
      </div>

    </div>
  </footer>
</template>

<style scoped lang="scss">
.bottom-menu {
  --footer-scale: 1.04;
  --footer-tile-width: 96px;
  --footer-round-size: 52px;
  --footer-feature-icon-size: 26px;
  --footer-feature-text-size: 15px;
  --footer-mode-text-size: 16px;
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 1100;
  width: max-content;
  margin: 10px auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  background: rgba(34, 34, 36, 0.8);
  transform: translateX(-50%) scale(var(--footer-scale));
  transform-origin: center bottom;
}

.bottom-menu.is-preview {
  z-index: 10000;
}

.footer-menu {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
}

.footer-menu-container {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
}

.icon-box {
  display: flex;
  width: var(--footer-tile-width);
  height: 74px;
  margin-left: 6px;
  padding: 14px 8px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.75);
  border-radius: 18px;
  background: url('/img/footer/footer_icon.png') no-repeat 0 0 / 100% 100%;
  cursor: pointer;

  &:hover {
    background-image: url('/img/footer/footer_icon_hover.png');
  }

  &.selected,
  &.icon-box-on {
    background-image: url('/img/footer/on_icon_bg.png');
  }

  &.selected:hover,
  &.icon-box-on:hover {
    background-image: url('/img/footer/on_icon_bg_hover.png');
  }

  >a {
    display: block;
    width: 26px;
    height: 26px;
    margin: 0 auto 6px;
    line-height: 0;
  }

  >a>img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.footer-menu>.footer-menu-container:first-child .icon-box {
  margin-left: 0;
}

.icon-box.icon-box-on>a {
  width: 29px;
  height: 29px;
  margin: 0;
}

.footer-icon {
  display: block;
  width: 26px;
  height: 26px;
  opacity: 0.8;
}

.footer-EQ-on {
  content: url('/img/footer/footer_EQ_on.png');
}

.footer-RGB-on {
  content: url('/img/footer/footer_RGB_on.png');
}

.footer-ENC-on {
  content: url('/img/footer/footer_ENCSwitch_on.png');
}

.footer-text {
  display: block;
  width: 100%;
  min-width: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

.footer-text.noise-level {
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 2px;
}

.footer-text :deep(.unit) {
  font-size: 10px;
}

.round-control {
  width: 52px;
  height: 52px;
}

.volume-container {
  margin: 0 5px 0 10px;
}

.mic-container {
  margin: 0 5px;
}

.sidetone-control {
  width: 54px;
  height: 54px;
  margin: 0 8px;
}

.hover-button,
.hover-button>a {
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  line-height: 0;
}

.hover-button {
  cursor: pointer;
}

.speaker-icon {
  width: 52px;
  height: 52px;
  opacity: 1;
}

.hover-button:hover .speaker-icon:not(.muted) {
  content: url('/img/footer/footer_voice_hover.png');
}

.hover-button:hover .speaker-icon.muted {
  content: url('/img/footer/footer_voice_hover_off.png');
}

.mic-container .hover-button {
  background: rgba(0, 0, 0, 0.4);
}

.mic-container .hover-button:hover {
  background: rgba(255, 255, 255, 0.09);
}

.mic-icon {
  width: 26px;
  height: 26px;
  margin: 2px;
  opacity: 1;
}

.hover-button:hover .mic-icon:not(.muted) {
  content: url('/img/footer/footer_mic_hover.png');
}

.hover-button:hover .mic-icon.muted {
  content: url('/img/footer/footer_mic_hover_off.png');
}

.audio-mode-container {
  margin-left: 11px;
}

.audio-mode-segment {
  position: relative;
  display: grid;
  width: 258px;
  height: 48px;
  padding: 3px;
  grid-template-columns: 80px 80px 92px;
  align-items: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: transparent;
}

.audio-mode-active-pill {
  position: absolute;
  top: 2px;
  left: 0;
  z-index: 0;
  width: var(--active-width);
  height: 42px;
  border-radius: 14px;
  background: rgba(55, 107, 237, 1);
  transform: translateX(var(--active-left));
  transition: width 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.audio-mode-btn {
  position: relative;
  z-index: 1;
  height: 42px;
  padding: 0 12px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font: inherit;
  font-size: var(--footer-mode-text-size);
  line-height: 42px;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: color 0.18s ease;
}

.audio-mode-btn:hover {
  color: rgba(255, 255, 255, 0.8);
}

.audio-mode-btn.is-active {
  color: rgba(205, 218, 250, 1);
}

.audio-mode-btn:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 11px;
  right: 0;
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
}

.audio-mode-btn.is-active::after {
  display: none;
}

.audio-balance-popup {
  position: absolute;
  bottom: calc(100% + 30px);
  left: 50%;
  z-index: 20;
  width: 260px;
  padding: 16px 18px 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(20px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%);
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

.audio-balance-popup::after,
.audio-info-popup::after,
.audio-feature-tooltip::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: -30px;
  left: 0;
  height: 30px;
  background: transparent;
}

.audio-mode-container:hover .audio-balance-popup,
.audio-mode-container.is-popup-open .audio-balance-popup {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.audio-balance-labels {
  display: grid;
  width: 100%;
  margin-bottom: 16px;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1;
}

.audio-balance-left {
  justify-self: end;
  text-align: right;
}

.audio-balance-right {
  justify-self: start;
  text-align: left;
}

.audio-balance-divider {
  margin: 0 12px;
  color: rgba(255, 255, 255, 0.2);
}

.audio-label {
  color: rgba(255, 255, 255, 0.28);
}

.audio-balance-slider {
  --ab-thumb-size: 20px;
  --ab-thumb-half: calc(var(--ab-thumb-size) / 2);
  --ab-pos: 50;
  --ab-fill-start: 50;
  --ab-fill-width: 0;
  position: relative;
  width: 100%;
  height: 22px;
  cursor: pointer;
  user-select: none;
  touch-action: none;
}

.audio-balance-track,
.audio-balance-fill {
  position: absolute;
  top: 50%;
  height: 8px;
  border-radius: 999px;
  transform: translateY(-50%);
}

.audio-balance-track {
  left: var(--ab-thumb-half);
  width: calc(100% - var(--ab-thumb-size));
  background: rgba(24, 24, 30, 0.9);
}

.audio-balance-fill {
  left: calc(var(--ab-thumb-half) + (var(--ab-fill-start) / 100) * (100% - var(--ab-thumb-size)));
  width: calc((var(--ab-fill-width) / 100) * (100% - var(--ab-thumb-size)));
  background: rgba(55, 107, 237, 1);
}

.audio-balance-thumb {
  position: absolute;
  top: 50%;
  left: calc(var(--ab-thumb-half) + (var(--ab-pos) / 100) * (100% - var(--ab-thumb-size)));
  z-index: 3;
  width: var(--ab-thumb-size);
  height: var(--ab-thumb-size);
  border: 3px solid rgba(255, 255, 255, 0.96);
  border-radius: 50%;
  background: rgba(55, 107, 237, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  cursor: grab;
  transform: translate(-50%, -50%);
  touch-action: none;
}

.audio-mode-container.is-popup-open .audio-balance-thumb {
  cursor: grabbing;
}

.help-container {
  width: 28px;
  height: 48px;
  margin-right: 2px;
  margin-left: 4px;
  justify-content: center;
}

.audio-help-button {
  display: block;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.08);
  color: rgba(255, 255, 255, 0.45);
  font: inherit;
  font-size: 18px;
  line-height: 25px;
  cursor: pointer;
}

.audio-help-button:hover,
.help-container.is-pinned .audio-help-button {
  border-color: rgba(255, 255, 255, 0.45);
  color: rgba(255, 255, 255, 0.78);
}

.audio-info-popup,
.audio-feature-tooltip {
  position: absolute;
  bottom: calc(100% + 30px);
  left: 50%;
  z-index: 30;
  border-radius: 16px;
  background: rgba(52, 52, 56, 0.98);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.26);
  color: rgba(255, 255, 255, 0.92);
  transform: translateX(-50%);
}

.audio-info-popup h3,
.audio-feature-tooltip h3 {
  margin: 0 0 10px;
  color: rgba(255, 255, 255, 0.94);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

.audio-info-popup p,
.audio-feature-tooltip p {
  margin: 0;
  color: rgba(184, 187, 199, 0.78);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;
  letter-spacing: 0;
}

.audio-modes-help-popup {
  width: 420px;
  padding: 22px 24px 24px;
}

.audio-mode-help-cards {
  display: grid;
  margin-top: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.audio-mode-help-card {
  min-height: 112px;
  padding: 16px 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}

.audio-mode-help-card strong,
.audio-mode-help-card span {
  display: block;
}

.audio-mode-help-card strong {
  margin-bottom: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.25;
}

.audio-mode-help-card span {
  color: rgba(184, 187, 199, 0.72);
  font-size: 13px;
  line-height: 1.35;
}

.audio-feature-item {
  margin-left: 15px;
}

.audio-feature-btn {
  display: inline-flex;
  min-width: 110px;
  height: 48px;
  padding: 0 17px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 0.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--footer-feature-text-size);
  line-height: 1;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.share-feature-btn {
  min-width: 132px;
}

.chiji-mode-btn {
  min-width: 128px;
}

.audio-feature-icon {
  display: block;
  width: var(--footer-feature-icon-size);
  height: var(--footer-feature-icon-size);
  flex: 0 0 var(--footer-feature-icon-size);
  color: currentColor;
}

.audio-feature-btn:hover {
  border-color: rgba(255, 255, 255, 0.4);
  color: rgba(255, 255, 255, 0.8);
}

.audio-feature-btn.is-active {
  border-color: rgba(55, 107, 237, 1);
  background: rgba(55, 107, 237, 1);
  color: rgba(205, 218, 250, 1);
}

.audio-feature-btn.is-active:hover {
  border-color: rgba(95, 137, 241, 1);
  background: rgba(95, 137, 241, 1);
  color: rgba(255, 255, 255, 0.85);
}

.audio-feature-btn.is-active :deep([stroke]) {
  stroke: currentColor;
  stroke-opacity: 1;
}

.audio-feature-btn.is-active :deep([fill]:not([fill='none'])) {
  fill: currentColor;
  fill-opacity: 1;
}

.audio-feature-tooltip {
  padding: 20px 26px 22px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%) translateY(6px);
  transition: none;
}

.bridge-feature-item .audio-feature-tooltip {
  width: 366px;
}

.share-feature-item .audio-feature-tooltip {
  width: 348px;
}

.audio-feature-item:hover>.audio-feature-tooltip {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.popup-dropdown,
.popup-window {
  position: absolute;
  bottom: calc(100% + 14px);
  left: 50%;
  z-index: 1001;
  // border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: #1a1a1f;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transform: translateX(-50%);
}

.popup-dropdown {
  min-width: 120px;
  padding: 8px 0;
  // 仅调整 EQ / RGB 下拉菜单，不影响音量、麦克风等 popup-window。
  background: #303033;
}

.dropdown-item {
  padding: 8px 10px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
}

.dropdown-item:hover:not(.active) {
  background: rgba(255, 255, 255, 0.05);
}

.dropdown-item.active,
.dropdown-item.active:hover {
  background: #376BED;
  color: rgba(255, 255, 255, 1);
}

.popup-window {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/*
 * 喇叭 / 麦克风音量悬浮窗单独还原旧版视觉。
 * 不复用 EQ/RGB 的 popup，也不影响耳返和 Audio Mix。
 */
.volume-container .popup-window,
.mic-container .popup-window {
  /*
   * 正确稿的悬浮窗本体约 55 × 194px：窄而高。
   * width 是“整个弹窗”的宽度，不能再使用 content-box + 25px 左右 padding，
   * 否则实际宽度会膨胀到 105px，正是上一版看起来过宽的原因。
   */
  bottom: calc(100% + 22px);
  left: 50%;
  width: 55px;
  height: 194px;
  box-sizing: border-box;
  padding: 18px 0 20px;
  gap: 10px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.1);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: none;
}

.volume-container .popup-window .slider-container,
.mic-container .popup-window .slider-container {
  display: flex;
  width: 100%;
  height: 130px;
  flex: 0 0 130px;
  padding: 0;
  align-items: center;
  justify-content: center;
}

.volume-container .popup-window :deep(.el-slider),
.mic-container .popup-window :deep(.el-slider) {
  --el-slider-main-bg-color: #376BED;
  --el-slider-runway-bg-color: rgba(35, 35, 41, 1);
  --el-slider-height: 6px;
  --el-slider-button-size: 16px;
}

.volume-container .popup-window :deep(.el-slider.is-vertical .el-slider__runway),
.mic-container .popup-window :deep(.el-slider.is-vertical .el-slider__runway) {
  width: 6px;
  margin: 0;
  background: rgba(35, 35, 41, 1);
  border-radius: 3px;
}

.volume-container .popup-window :deep(.el-slider.is-vertical .el-slider__bar),
.mic-container .popup-window :deep(.el-slider.is-vertical .el-slider__bar) {
  width: 6px;
  background: #376BED;
  border-radius: 3px;
}

.volume-container .popup-window :deep(.el-slider__button),
.mic-container .popup-window :deep(.el-slider__button) {
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  background: #376BED;
  box-shadow: none;
}

.volume-container .popup-window :deep(.el-slider__button:hover),
.volume-container .popup-window :deep(.el-slider__button.hover),
.volume-container .popup-window :deep(.el-slider__button.dragging),
.mic-container .popup-window :deep(.el-slider__button:hover),
.mic-container .popup-window :deep(.el-slider__button.hover),
.mic-container .popup-window :deep(.el-slider__button.dragging) {
  transform: none;
}

.popup-window .slider-container {
  padding: 8px;
}

.popup-window .value-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.volume-container .popup-window .value-text,
.mic-container .popup-window .value-text {
  width: 100%;
  margin: 0;
  color: rgba(255, 255, 255, 1);
  font-size: 13px;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}

@media (max-width: 1600px) {
  .bottom-menu {
    --footer-scale: 0.93;
  }
}

@media (max-width: 1440px) {
  .bottom-menu {
    --footer-scale: 0.87;
  }
}

@media (max-width: 1280px) {
  .bottom-menu {
    --footer-scale: 0.76;
  }
}
</style>
